import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Select, Table, Drawer } from 'antd';
import { Eye, AlertTriangle } from 'lucide-react';
import { toast } from '@rickylandino/react-messages';
import { firstBy } from 'thenby';
import Globals from '../../../../config/globals';
import { getExamTypesByFacilityForMWL, getProvidersByFacility, getCategoryExamTypes } from '../../../../common/services/FacilityService';
import { allTechnologists, allCategories } from '../../../../common/services/ApiService';
import { getUnperformedExamsForReconcile } from '../../../../common/services/next-gen/WidgetDataService';
import { useReconcileExamPerformFailure } from '../../../../hooks/useWidgetData';
import { groupBy } from '../../../Helpers/GroupBy';

const { Option, OptGroup } = Select;

const EMPTY_CORRECTIONS = { examTypeId: '', performingTechUserId: '', providerId: '' };
const EMPTY_REQUIRED = { examTypeId: false, performingTechUserId: false, providerId: false };
const EMPTY_LINKED_UNRESOLVABLE = { examTypeId: false, performingTechUserId: false, providerId: false };
const EMPTY_RESOLVED_NAMES = { examTypeId: null, performingTechUserId: null, providerId: null };
const KEEP = '__keep__';

/**
 * ReconcilePerformFailureModal
 * Shared dialog used by both the system and filtered Exam Perform Failures widgets.
 * Lets the user link a failure to an unperformed exam and optionally apply field corrections.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Object|null} props.failure - The exam perform failure record
 * @param {Function} props.onSuccess - Called after a successful reconcile
 * @param {Function} props.onCancel
 */
export default function ReconcilePerformFailureModal({ visible, failure, onSuccess, onCancel }) {
    const reconcileMutation = useReconcileExamPerformFailure();

    // Supporting data
    const [categories, setCategories] = useState([]);
    const [technologists, setTechnologists] = useState(undefined); // undefined until first BehaviorSubject emit
    const [examTypesData, setExamTypesData] = useState(undefined); // Phase 1 resolution (MWL-filtered list)
    const [configExamTypes, setConfigExamTypes] = useState(undefined); // Phase 2 + dropdown options (full active list)
    const [examTypeOptions, setExamTypeOptions] = useState([]);
    const [providers, setProviders] = useState(undefined); // undefined until loaded
    const [isLoadingSupporting, setIsLoadingSupporting] = useState(false);

    // Phase guards � prevent Phase 1/2 from re-running for the same record/exam
    const lastInitializedTargetId = useRef(null);
    const autoFilledForExamId = useRef(null);

    // Unperformed exams picker
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [unperformedExams, setUnperformedExams] = useState([]);
    const [isLoadingExams, setIsLoadingExams] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);

    // Corrections
    const [corrections, setCorrections] = useState(EMPTY_CORRECTIONS);
    const [requiredFields, setRequiredFields] = useState(EMPTY_REQUIRED);
    const [linkedUnresolvable, setLinkedUnresolvable] = useState(EMPTY_LINKED_UNRESOLVABLE);
    const [resolvedNames, setResolvedNames] = useState(EMPTY_RESOLVED_NAMES);

    // View drawer
    const [viewExam, setViewExam] = useState(null);
    const [showViewDrawer, setShowViewDrawer] = useState(false);

    // Subscribe to allTechnologists and allCategories BehaviorSubjects once
    // technologists starts as undefined; first emit sets it to the mapped array
    useEffect(() => {
        const techSub = allTechnologists.subscribe(list => {
            setTechnologists((list || []).map(t => ({
                ...t,
                name: t.fName ? `${t.fName} ${t.lName || ''}`.trim() : (t.name || t.userID)
            })));
        });
        const catSub = allCategories.subscribe(list => setCategories(list || []));
        return () => {
            techSub.unsubscribe();
            catSub.unsubscribe();
        };
    }, []);

    // Debounce search input (200ms per spec)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchInput), 200);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Reset and load data when modal opens for a new failure
    useEffect(() => {
        if (!visible || !failure) return;
        setSelectedExam(null);
        setCorrections(EMPTY_CORRECTIONS);
        setRequiredFields(EMPTY_REQUIRED);
        setLinkedUnresolvable(EMPTY_LINKED_UNRESOLVABLE);
        setResolvedNames(EMPTY_RESOLVED_NAMES);
        setSearchInput('');
        setDebouncedSearch('');
        setExamTypesData(undefined);
        setConfigExamTypes(undefined);
        setProviders(undefined);
        setExamTypeOptions([]);
        lastInitializedTargetId.current = null;
        autoFilledForExamId.current = null;
        loadSupportingData();
    }, [visible, failure?.examPerformFailureId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load unperformed exams whenever search changes
    useEffect(() => {
        if (!visible || !failure) return;
        loadUnperformedExams(debouncedSearch);
    }, [debouncedSearch, visible, failure?.examPerformFailureId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSupportingData = async () => {
        if (!failure?.facilityId) return;
        setIsLoadingSupporting(true);
        try {
            const [examTypesResponse, providersResponse, catExamTypes] = await Promise.all([
                getExamTypesByFacilityForMWL(failure.facilityId),
                getProvidersByFacility(failure.facilityId),
                getCategoryExamTypes(failure.facilityId)
            ]);

            // examTypesData: MWL-filtered list used for Phase 1 ID resolution
            const flat = examTypesResponse?.data || [];
            setExamTypesData(flat);

            // configExamTypes: full active list used for Phase 2 matching and dropdown options
            const catTypes = catExamTypes || [];
            setConfigExamTypes(catTypes);
            setExamTypeOptions(buildExamTypeOptions(catTypes));

            const provList = (providersResponse || []).map(p => ({
                ...p,
                name: p.fName ? `${p.fName} ${p.lName || ''}`.trim() : (p.providerName || p.providerID)
            }));
            setProviders(provList);
        } catch (e) {
            console.error('Error loading reconcile supporting data:', e);
        } finally {
            setIsLoadingSupporting(false);
        }
    };

    const buildExamTypeOptions = useCallback((flat) => {
        if (!flat || !flat.length) return [];
        const sorted = [...flat].sort(firstBy('categoryID'));
        const grouped = groupBy(sorted, 'categoryID');
        const opts = [];
        for (const catId in grouped) {
            grouped[catId].sort(firstBy('examType'));
            opts.push({
                catId,
                options: grouped[catId].map(et => ({ label: et.examType, value: et.examTypeID }))
            });
        }
        return opts;
    }, []);

    const loadUnperformedExams = async (search) => {
        if (!failure) return;
        setIsLoadingExams(true);
        try {
            const data = await getUnperformedExamsForReconcile(search, failure.facilityId);
            setUnperformedExams(data?.data || data || []);
        } catch (e) {
            console.error('Error loading unperformed exams:', e);
            setUnperformedExams([]);
        } finally {
            setIsLoadingExams(false);
        }
    };

    // Phase 1: Check ORU-provided IDs against lookup lists when modal opens for a new failure.
    // Uses examTypesData (MWL-filtered) for exam type, same lists for tech/provider.
    // Guarded by lastInitializedTargetId so it never re-runs for the same failure record.
    useEffect(() => {
        if (!visible || !failure) return;
        if (examTypesData === undefined || technologists === undefined || providers === undefined) return;
        if (lastInitializedTargetId.current === failure.examPerformFailureId) return;
        lastInitializedTargetId.current = failure.examPerformFailureId;

        const findById = (id, list, idKey) => list.find(item => item[idKey] === id) || null;

        const examTypeRecord = failure.examTypeId ? findById(failure.examTypeId, examTypesData, 'examTypeID') : null;
        const techRecord = failure.performingTechUserId ? findById(failure.performingTechUserId, technologists, 'userID') : null;
        const providerRecord = failure.providerId ? findById(failure.providerId, providers, 'providerID') : null;

        setCorrections({
            examTypeId: failure.examTypeId ? (examTypeRecord ? KEEP : '') : '',
            performingTechUserId: failure.performingTechUserId ? (techRecord ? KEEP : '') : '',
            providerId: failure.providerId ? (providerRecord ? KEEP : '') : '',
        });
        setRequiredFields({
            examTypeId: !!failure.examTypeId && !examTypeRecord,
            performingTechUserId: !!failure.performingTechUserId && !techRecord,
            providerId: !!failure.providerId && !providerRecord,
        });
        setResolvedNames({
            examTypeId: examTypeRecord ? (failure.examType || examTypeRecord.examType || null) : null,
            performingTechUserId: techRecord ? (failure.performingTechName || techRecord.name || null) : null,
            providerId: providerRecord ? (failure.providerName || providerRecord.name || null) : null,
        });
    }, [visible, failure?.examPerformFailureId, examTypesData, technologists, providers]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 2: Re-check IDs and auto-fill from linked exam after the user selects one.
    // Uses configExamTypes (full active list) for exam type ID check AND name matching.
    // Uses technologists/providers (same lists as Phase 1) for name matching.
    // Guarded by autoFilledForExamId so user corrections are never overwritten on re-render.
    useEffect(() => {
        if (!selectedExam || !failure) return;
        if (configExamTypes === undefined || technologists === undefined || providers === undefined) return;
        if (autoFilledForExamId.current === selectedExam.patientExamID) return;
        autoFilledForExamId.current = selectedExam.patientExamID;

        // ID resolution using Phase 2 lookup lists
        const examTypeRecord2 = failure.examTypeId
            ? configExamTypes.find(et => et.examTypeID === failure.examTypeId) || null
            : null;
        const techRecord2 = failure.performingTechUserId
            ? technologists.find(t => t.userID === failure.performingTechUserId) || null
            : null;
        const providerRecord2 = failure.providerId
            ? providers.find(p => p.providerID === failure.providerId) || null
            : null;

        // Auto-fill by name when ORU provided null for a field
        const examTypeAutoFill = !failure.examTypeId && selectedExam.examType
            ? configExamTypes.find(et => et.examType === selectedExam.examType) || null
            : null;
        const techAutoFill = !failure.performingTechUserId && selectedExam.performingTechName
            ? technologists.find(t => t.name === selectedExam.performingTechName) || null
            : null;
        // Provider name match: exact "fName lName" join per spec (no trim)
        const providerAutoFill = !failure.providerId && selectedExam.providerName
            ? providers.find(p => `${p.fName} ${p.lName || ''}` === selectedExam.providerName) || null
            : null;

        const newExamTypeId = failure.examTypeId
            ? (examTypeRecord2 ? KEEP : '')
            : (examTypeAutoFill ? examTypeAutoFill.examTypeID : '');
        const newTechId = failure.performingTechUserId
            ? (techRecord2 ? KEEP : '')
            : (techAutoFill ? techAutoFill.userID : '');
        const newProviderId = failure.providerId
            ? (providerRecord2 ? KEEP : '')
            : (providerAutoFill ? providerAutoFill.providerID : '');

        // Scenario 2: ORU null, linked exam has a value that couldn't be name-matched (advisory)
        const examTypeLinkedUnresolvable = !failure.examTypeId && !!selectedExam.examType && !examTypeAutoFill;
        const techLinkedUnresolvable = !failure.performingTechUserId && !!selectedExam.performingTechName && !techAutoFill;
        const providerLinkedUnresolvable = !failure.providerId && !!selectedExam.providerName && !providerAutoFill;

        setCorrections({
            examTypeId: newExamTypeId,
            performingTechUserId: newTechId,
            providerId: newProviderId,
        });
        setRequiredFields({
            examTypeId: !!failure.examTypeId && !examTypeRecord2,
            performingTechUserId: !!failure.performingTechUserId && !techRecord2,
            providerId: !!failure.providerId && !providerRecord2,
        });
        setLinkedUnresolvable({
            examTypeId: examTypeLinkedUnresolvable,
            performingTechUserId: techLinkedUnresolvable,
            providerId: providerLinkedUnresolvable,
        });
        setResolvedNames({
            examTypeId: examTypeRecord2 ? (failure.examType || examTypeRecord2.examType || null) : null,
            performingTechUserId: techRecord2 ? (failure.performingTechName || techRecord2.name || null) : null,
            providerId: providerRecord2 ? (failure.providerName || providerRecord2.name || null) : null,
        });
    }, [selectedExam?.patientExamId, configExamTypes, technologists, providers, failure?.examPerformFailureId]); // eslint-disable-line react-hooks/exhaustive-deps

    const canReconcile = !!selectedExam &&
        !Object.keys(requiredFields).some(k => requiredFields[k] && corrections[k] === '') &&
        !reconcileMutation.isLoading;

    const handleReconcile = useCallback(async () => {
        if (!canReconcile) return;
        try {
            const normalize = (val) => (val === KEEP || val === '' || val === null) ? null : val;
            await reconcileMutation.mutateAsync({
                examPerformFailureId: failure.examPerformFailureId,
                patientExamId: selectedExam.patientExamID,
                examTypeId: normalize(corrections.examTypeId),
                performingTechUserId: normalize(corrections.performingTechUserId),
                providerId: normalize(corrections.providerId)
            });
            toast.success('Failure reconciled successfully');
            onSuccess();
        } catch (e) {
            console.error('Reconcile error:', e);
            toast.error('Failed to reconcile. Please try again.');
        }
    }, [canReconcile, failure, selectedExam, corrections, reconcileMutation, onSuccess]);

    const handleViewExam = useCallback((exam) => {
        setViewExam(exam);
        setShowViewDrawer(true);
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const getOruDisplay = (id, name) => {
        if (!id && !name) return null; // null means "�"
        return name || id; // if no name, show raw ID (will be styled amber since unresolvable)
    };

    const getDropdownValue = (fieldKey) => {
        const val = corrections[fieldKey];
        if (val === null || val === '') return undefined; // show placeholder
        return val; // KEEP or an actual ID
    };

    const pink = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const pinkHover = Globals.theme === 'dark' ? '#db2777' : '#be185d';

    // Unperformed exams picker columns
    const examPickerColumns = [
        {
            title: 'Patient',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 150,
            render: (name, row) => name || row.mrn || '-'
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            width: 90,
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{mrn || '-'}</span>
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 160,
        },
        {
            title: 'Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            width: 110,
            render: (d) => formatDate(d)
        },
        {
            title: '',
            key: 'view',
            width: 80,
            render: (_, row) => (
                <button
                    onClick={(e) => { e.stopPropagation(); handleViewExam(row); }}
                    className="px-2 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                    style={{ backgroundColor: pink, color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = pinkHover}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = pink}
                >
                    <Eye size={13} /> View
                </button>
            )
        }
    ];

    const correctionFields = [
        {
            label: 'Exam Type',
            oruValue: failure?.examTypeId ? (resolvedNames.examTypeId || failure?.examTypeId) : null,
            oruResolved: !!resolvedNames.examTypeId,
            linkedValue: selectedExam?.examType || '-',
            correctionKey: 'examTypeId',
            options: (
                <>
                    {resolvedNames.examTypeId && (
                        <OptGroup label="Current Value">
                            <Option value={KEEP}>{resolvedNames.examTypeId}</Option>
                        </OptGroup>
                    )}
                    {examTypeOptions.map(group => (
                        <OptGroup
                            key={group.catId}
                            label={categories.find(c => c.categoryID === group.catId)?.description || group.catId}
                        >
                            {group.options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                        </OptGroup>
                    ))}
                </>
            )
        },
        {
            label: 'Performing Tech',
            oruValue: failure?.performingTechUserId ? (resolvedNames.performingTechUserId || failure?.performingTechUserId) : null,
            oruResolved: !!resolvedNames.performingTechUserId,
            linkedValue: selectedExam?.performingTechName || '-',
            correctionKey: 'performingTechUserId',
            options: (
                <>
                    {resolvedNames.performingTechUserId && (
                        <OptGroup label="Current Value">
                            <Option value={KEEP}>{resolvedNames.performingTechUserId}</Option>
                        </OptGroup>
                    )}
                    {(technologists || []).map(t => <Option key={t.userID} value={t.userID}>{t.name}</Option>)}
                </>
            )
        },
        {
            label: 'Provider',
            oruValue: failure?.providerId ? (resolvedNames.providerId || failure?.providerId) : null,
            oruResolved: !!resolvedNames.providerId,
            linkedValue: selectedExam?.providerName || '-',
            correctionKey: 'providerId',
            options: (
                <>
                    {resolvedNames.providerId && (
                        <OptGroup label="Current Value">
                            <Option value={KEEP}>{resolvedNames.providerId}</Option>
                        </OptGroup>
                    )}
                    {(providers || []).map(p => <Option key={p.providerID} value={p.providerID}>{p.name}</Option>)}
                </>
            )
        }
    ];

    const modalFooter = [
        <button
            key="cancel"
            onClick={onCancel}
            className="px-4 py-1.5 rounded-lg font-medium text-sm"
            style={{
                backgroundColor: 'transparent',
                border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                cursor: 'pointer',
                marginRight: 8
            }}
        >
            Cancel
        </button>,
        <button
            key="reconcile"
            onClick={handleReconcile}
            disabled={!canReconcile}
            className="px-4 py-1.5 rounded-lg font-medium text-sm"
            style={{
                backgroundColor: canReconcile ? pink : (Globals.theme === 'dark' ? '#3f3f46' : '#e4e4e7'),
                color: canReconcile ? 'white' : (Globals.theme === 'dark' ? '#71717a' : '#a1a1aa'),
                border: 'none',
                cursor: canReconcile ? 'pointer' : 'not-allowed'
            }}
            onMouseEnter={e => { if (canReconcile) e.currentTarget.style.backgroundColor = pinkHover; }}
            onMouseLeave={e => { if (canReconcile) e.currentTarget.style.backgroundColor = pink; }}
        >
            Reconcile
        </button>
    ];

    if (!failure) return null;

    return (
        <>
            <Modal
                visible={visible}
                title="Reconcile Perform Failure"
                onCancel={onCancel}
                footer={modalFooter}
                width={920}
                bodyStyle={{ overflowY: 'auto', maxHeight: '75vh', padding: '16px 24px' }}
                destroyOnClose
            >
                {/* Accession + failure reason subtitle */}
                <p style={{ marginBottom: 16, fontSize: '0.875rem' }}>
                    Accession {failure.accessionNumber || '-'} -{' '}
                    <span style={{ color: '#ef4444', fontWeight: 500 }}>{failure.failureReason}</span>
                </p>

                {/* Unperformed exam picker */}
                <div style={{ marginBottom: 16 }}>
                    <p style={{ fontWeight: 600, marginBottom: 8 }}>Link to Unperformed Exam</p>
                    <input
                        type="text"
                        placeholder="Search by patient name, MRN, or exam type..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '6px 12px',
                            marginBottom: 8,
                            border: `1px solid ${pink}`,
                            borderRadius: 6,
                            outline: 'none',
                            fontSize: '0.875rem',
                            backgroundColor: Globals.theme === 'dark' ? '#18181b' : '#fff',
                            color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b'
                        }}
                    />
                    <Table
                        columns={examPickerColumns}
                        dataSource={unperformedExams}
        rowKey="patientExamID"
                        size="small"
                        loading={isLoadingExams || isLoadingSupporting}
                        scroll={{ y: 180 }}
                        pagination={false}
                        onRow={(row) => ({
                            onClick: () => setSelectedExam(row),
                            style: {
                                cursor: 'pointer',
                            backgroundColor: selectedExam?.patientExamID === row.patientExamID
                    ? (Globals.theme === 'dark' ? 'rgba(236,72,153,0.15)' : 'rgba(219,39,119,0.08)')
                                    : undefined
                            }
                        })}
                    />
                </div>

                {/* Corrections grid - shown only after an exam is selected */}
                {selectedExam && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <p style={{ fontWeight: 600, margin: 0 }}>Corrections</p>
                            <span style={{ fontSize: '0.75rem', color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                                Choose to keep the existing value or select a different one
                            </span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${Globals.theme === 'dark' ? '#3f3f46' : '#e4e4e7'}` }}>
                                    {['FIELD', 'ORU PROVIDED', 'LINKED EXAM', 'CORRECTION'].map(h => (
                                        <th key={h} style={{
                                            padding: '6px 12px',
                                            textAlign: 'left',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                                            background: Globals.theme === 'dark' ? '#27272a' : '#f9fafb'
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                            {correctionFields.map(field => {
                                    // Scenario 1: ORU provided an ID that can't be resolved � blocks reconcile
                                    const isScenario1 = requiredFields[field.correctionKey] && corrections[field.correctionKey] === '';
                                    // Scenario 2: ORU was null, linked exam value couldn't be name-matched � advisory only
                                    const isScenario2 = linkedUnresolvable[field.correctionKey] && corrections[field.correctionKey] === '';
                                    const showAmberRing = isScenario1 || isScenario2;

                                    return (
                                        <tr key={field.correctionKey} style={{ borderBottom: `1px solid ${Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5'}` }}>
                                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>{field.label}</td>
                                            <td style={{ padding: '8px 12px' }}>
                                                {field.oruValue === null
                                                    ? <span style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>-</span>
                                                    : <span style={{
                                                        color: !field.oruResolved ? '#f59e0b' : 'inherit',
                                                        fontFamily: !field.oruResolved ? 'monospace' : 'inherit'
                                                    }}>
                                                        {field.oruValue}
                                                    </span>
                                                }
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>{field.linkedValue}</td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <Select
                                                    value={getDropdownValue(field.correctionKey)}
                                                    onChange={val => setCorrections(prev => ({ ...prev, [field.correctionKey]: val ?? null }))}
                                                    placeholder={isScenario1 ? 'Select a value before reconciling' : undefined}
                                                    style={{
                                                        width: '100%',
                                                        outline: showAmberRing ? '2px solid #f59e0b' : 'none',
                                                        borderRadius: 6
                                                    }}
                                                    allowClear
                                                    showSearch
                                                    optionFilterProp="children"
                                                >
                                                    {field.options}
                                                </Select>
                                                {isScenario2 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, color: '#f59e0b', fontSize: '0.75rem' }}>
                                                        <AlertTriangle size={12} />
                                                        The previously selected value is no longer available.
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            {/* Exam detail drawer */}
            <Drawer
                title={viewExam ? `${viewExam.patientName || viewExam.mrn || 'Exam'} - Detail` : 'Exam Detail'}
                placement="right"
                width={400}
                visible={showViewDrawer}
                onClose={() => { setShowViewDrawer(false); setViewExam(null); }}
                zIndex={1010}
            >
                {viewExam && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: '0.875rem' }}>
                        {[
                            ['Patient', viewExam.patientName || '-'],
                            ['MRN', viewExam.mrn || '-'],
                            ['Exam Type', viewExam.examType || '-'],
                            ['Facility', viewExam.facilityName || '-'],
                            ['Date', formatDate(viewExam.examDateTime)],
                            ['Provider', viewExam.providerName || '-'],
                            ['Performing Tech', viewExam.performingTechName || '-'],
                        ].map(([label, value]) => (
                            <div key={label} style={{ display: 'flex', gap: 8 }}>
                                <span style={{ fontWeight: 600, minWidth: 120, color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>{label}</span>
                                <span>{value}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>
        </>
    );
}
